import { useEffect, useRef } from 'react';
import { pushToDataLayer, GTMEvent, hasEventFiredThisSession, markEventAsFired } from '../utils/gtm';

interface ScrollTrackingConfig {
  event50: GTMEvent;
  event90: GTMEvent;
}

export const useScrollTracking = ({ event50, event90 }: ScrollTrackingConfig): void => {
  const has50Fired = useRef(false);
  const has90Fired = useRef(false);

  useEffect(() => {
    const sessionKey50 = `gtm_${event50}`;
    const sessionKey90 = `gtm_${event90}`;

    if (hasEventFiredThisSession(sessionKey50)) {
      has50Fired.current = true;
    }

    if (hasEventFiredThisSession(sessionKey90)) {
      has90Fired.current = true;
    }

    let ticking = false;

    const handleScroll = (): void => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const scrollTop = window.scrollY || document.documentElement.scrollTop;
          const scrollPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;

          if (scrollPercentage >= 50 && !has50Fired.current) {
            pushToDataLayer(event50);
            markEventAsFired(sessionKey50);
            has50Fired.current = true;
          }

          if (scrollPercentage >= 90 && !has90Fired.current) {
            pushToDataLayer(event90);
            markEventAsFired(sessionKey90);
            has90Fired.current = true;
          }

          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [event50, event90]);
};
